/**
 * VisualDebugger.js — Step-by-step Python execution visualizer
 * Layout:
 *   Top bar  : progress bar + step counter + Prev / Next + close
 *   Left     : code with highlighted current line
 *   Right top: variables in scope
 *   Right bot: output so far
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';

const formatValue = (val) => {
  if (val === null) return <span style={{ color: '#9ca3af' }}>None</span>;
  if (typeof val === 'boolean') return <span style={{ color: '#f97316' }}>{val ? 'True' : 'False'}</span>;
  if (typeof val === 'number') return <span style={{ color: '#60a5fa' }}>{val}</span>;
  if (typeof val === 'string') return <span style={{ color: '#34d399' }}>"{val}"</span>;
  if (Array.isArray(val)) return (
    <span style={{ color: '#c4b5fd' }}>
      [{val.map((v, i) => <React.Fragment key={i}>{i > 0 && ', '}{formatValue(v)}</React.Fragment>)}]
    </span>
  );
  if (val !== null && typeof val === 'object' && val.__type__ === 'tuple') return (
    <span style={{ color: '#c4b5fd' }}>
      {'('}{val.items.map((v, i) => <React.Fragment key={i}>{i > 0 && ', '}{formatValue(v)}</React.Fragment>)}{val.items.length === 1 ? ',' : ''}{')'} 
    </span>
  );
  if (val !== null && typeof val === 'object' && val.__type__ === 'set') return (
    <span style={{ color: '#f9a8d4' }}>
      {'{' + val.items.map(v => {
        if (v === null) return 'None';
        if (typeof v === 'boolean') return v ? 'True' : 'False';
        if (typeof v === 'string') return `"${v}"`;
        return String(v);
      }).join(', ') + '}'}
    </span>
  );
  if (val !== null && typeof val === 'object' && val.__type__ === 'object') return (
    <span style={{ color: '#fb923c' }}>
      {val.class}(
      {Object.entries(val.attrs || {}).map(([k, v], i) => (
        <React.Fragment key={i}>
          {i > 0 && ', '}
          <span style={{ color: '#86efac' }}>{k}</span>
          <span style={{ color: '#9ca3af' }}>=</span>
          {formatValue(v)}
        </React.Fragment>
      ))}
      )
    </span>
  );
  if (typeof val === 'object') {
    const entries = Object.entries(val);
    return (
      <span style={{ color: '#fbbf24' }}>
        {'{'}{entries.map(([k, v], i) => (
          <React.Fragment key={i}>
            {i > 0 && ', '}
            <span style={{ color: '#c4b5fd' }}>"{k}"</span>: {formatValue(v)}
          </React.Fragment>
        ))}{'}'}
      </span>
    );
  }
  return <span style={{ color: '#e5e7eb' }}>{String(val)}</span>;
};

const VisualDebugger = ({ code, steps, onClose }) => {
  const lines = code.split('\n');
  const totalLines = lines.length;

  // Only show steps that correspond to actual lines in the user's code (or end marker)
  const validSteps = steps.filter(s => s.end || (s.line >= 1 && s.line <= totalLines));
  const totalSteps = validSteps.length;
  const [stepIndex, setStepIndex] = useState(0);
  const activeLineRef = useRef(null);
  const currentStep = validSteps[stepIndex] || {};
  const currentLine = currentStep.line;

  // Internal tracer variables to hide from the student view
  const HIDDEN_VARS = new Set(['output_buffer', 'input_index', 'input_buffer', 'fake_input', 'tracer', 'input']);

  const variableEntries = Object.entries(currentStep.locals || {})
    .filter(([k]) => !HIDDEN_VARS.has(k));
  const prevLocals = stepIndex > 0 ? (validSteps[stepIndex - 1].locals || {}) : {};
  const changedVars = new Set(
    variableEntries
      .filter(([k, v]) => JSON.stringify(prevLocals[k]) !== JSON.stringify(v))
      .map(([k]) => k)
  );

  const prev = () => setStepIndex(i => Math.max(i - 1, 0));
  const next = () => setStepIndex(i => Math.min(i + 1, totalSteps - 1));

  const onKey = useCallback((e) => {
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') { e.preventDefault(); setStepIndex(i => Math.min(i + 1, totalSteps - 1)); }
    else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') { e.preventDefault(); setStepIndex(i => Math.max(i - 1, 0)); }
    else if (e.key === 'Escape') onClose();
  }, [totalSteps, onClose]);

  useEffect(() => {
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onKey]);

  useEffect(() => {
    activeLineRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [stepIndex]);

  const progress = ((stepIndex + 1) / totalSteps) * 100;
  const hasError = !!currentStep.error;
  // Detect truncation: heavy loop hit the step cap — no 'end' step recorded, no error
  const wasTruncated = totalSteps > 0
    && !validSteps.some(s => s.end)
    && !validSteps.some(s => s.error);

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0,
        backgroundColor: 'rgba(0,0,0,0.88)',
        zIndex: 11000,
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
        padding: '80px 20px 20px',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          width: '92vw', maxWidth: '1100px',
          height: '72vh',
          backgroundColor: '#0d1117',
          border: '2px solid rgba(220, 195, 90, 0.65)',
          borderRadius: '12px',
          display: 'flex', flexDirection: 'column',
          overflow: 'hidden',
          boxShadow: '0 0 0 1px rgba(255,240,150,0.15), 0 20px 50px rgba(0,0,0,0.7)',
        }}
      >
        {/* ── Top bar ── */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: '10px',
          padding: '7px 14px',
          backgroundColor: '#161b22',
          borderBottom: '1px solid #30363d',
          flexShrink: 0,
        }}>
          {/* Progress bar */}
          <div style={{
            flex: 1, height: '6px',
            backgroundColor: '#21262d', borderRadius: '3px', overflow: 'hidden',
          }}>
            <div style={{
              width: `${progress}%`, height: '100%',
              backgroundColor: hasError ? '#f85149' : '#00d4aa',
              borderRadius: '3px', transition: 'width 0.12s',
            }} />
          </div>

          {/* Step counter */}
          <span style={{ color: '#8b949e', fontSize: '17px', whiteSpace: 'nowrap' }}>
            Step <strong style={{ color: '#e6edf3' }}>{stepIndex + 1}</strong> / {totalSteps}
            {hasError && <span style={{ color: '#f85149', marginLeft: '10px' }}>⚠️ {currentStep.error}</span>}
            {currentStep.end && <span style={{ color: '#4ade80', marginLeft: '10px' }}>✓ Program complete</span>}
          </span>

          <button onClick={prev} disabled={stepIndex === 0} style={navBtn(stepIndex === 0)}>◀ Prev</button>
          <button onClick={next} disabled={stepIndex === totalSteps - 1} style={navBtn(stepIndex === totalSteps - 1)}>Next ▶</button>

          <button
            onClick={onClose}
            style={{ background: 'none', border: 'none', color: '#8b949e', fontSize: '24px', cursor: 'pointer', padding: '2px 8px', lineHeight: 1, borderRadius: '4px' }}
            onMouseOver={e => e.target.style.color = '#f85149'}
            onMouseOut={e => e.target.style.color = '#8b949e'}
          >×</button>
        </div>

        {/* ── Truncation warning banner ── */}
        {wasTruncated && (
          <div style={{
            backgroundColor: '#2d2000',
            borderBottom: '1px solid #6a4f00',
            padding: '6px 14px',
            fontSize: '13px',
            color: '#f0c040',
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}>
            ⚠️ <strong>Step limit reached</strong> — this loop has too many iterations to visualize fully.
            Only the first {totalSteps} steps are shown. Add a smaller loop range to see the full trace.
          </div>
        )}

        {/* ── Body ── */}
        <div style={{ display: 'flex', flex: 1, minHeight: 0, gap: '8px', padding: '8px' }}>

          {/* Code */}
          <div style={{
            flex: '0 0 58%', overflowY: 'auto', overflowX: 'auto',
            border: '1px solid rgba(255, 240, 150, 0.35)',
            borderRadius: '7px',
            fontFamily: 'Consolas, Monaco, "Courier New", monospace',
            fontSize: '19px', lineHeight: '1.7',
          }}>
            {lines.map((lineText, idx) => {
              const ln = idx + 1;
              const active = ln === currentLine && !currentStep.end;
              return (
                <div key={idx} ref={active ? activeLineRef : null} style={{
                  display: 'flex',
                  minWidth: 'max-content',
                  backgroundColor: active ? 'rgba(255,200,0,0.22)' : 'transparent',
                  borderLeft: active ? '3px solid #f5c400' : '3px solid transparent',
                }}>
                  <span style={{
                    minWidth: '42px', padding: '0 10px',
                    color: active ? '#ffd700' : '#4b5563',
                    textAlign: 'right', userSelect: 'none', fontSize: '16px',
                    flexShrink: 0,
                  }}>
                    {active ? '▶' : ln}
                  </span>
                  <span style={{
                    padding: '0 12px 0 4px',
                    color: active ? '#ffffff' : '#c9d1d9',
                    whiteSpace: 'pre',
                  }}>
                    {lineText || ' '}
                  </span>
                </div>
              );
            })}
          </div>

          {/* Right: variables + output */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, gap: '8px' }}>

            {/* Variables */}
            <div style={{ flex: '0 0 55%', display: 'flex', flexDirection: 'column', border: '1px solid rgba(255, 240, 150, 0.35)', borderRadius: '7px', overflow: 'hidden' }}>
              <div style={{
                padding: '5px 12px', backgroundColor: '#161b22',
                borderBottom: '1px solid rgba(255, 240, 150, 0.2)',
                fontSize: '15px', fontWeight: 700, letterSpacing: '1px',
                color: '#e8d48a', textTransform: 'uppercase', flexShrink: 0,
              }}>Variables</div>
              <div style={{ flex: 1, overflowY: 'auto' }}>
                {variableEntries.length === 0
                  ? <div style={{ color: '#4b5563', fontSize: '17px', padding: '12px 16px' }}>No variables yet</div>
                  : (
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '17px' }}>
                      <tbody>
                        {variableEntries.map(([name, val]) => {
                          const changed = changedVars.has(name);
                          return (
                            <tr key={name} style={{
                              backgroundColor: changed ? 'rgba(255,215,0,0.06)' : 'transparent',
                              borderBottom: '1px solid #161b22',
                            }}>
                              <td style={{
                                padding: '3px 12px',
                                color: changed ? '#ffd700' : '#86efac',
                                fontFamily: 'monospace', fontWeight: changed ? 700 : 400,
                                whiteSpace: 'nowrap', width: '35%',
                              }}>
                                {changed && <span style={{ marginRight: '5px' }}>★</span>}{name}
                              </td>
                              <td style={{ padding: '3px 6px', color: '#9ca3af' }}>=</td>
                              <td style={{ padding: '3px 12px 3px 0', fontFamily: 'monospace', wordBreak: 'break-all' }}>
                                {formatValue(val)}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )
                }
              </div>
            </div>

            {/* Output */}
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, border: '1px solid rgba(255, 240, 150, 0.35)', borderRadius: '7px', overflow: 'hidden' }}>
              <div style={{
                padding: '5px 12px', backgroundColor: '#161b22',
                borderBottom: '1px solid rgba(255, 240, 150, 0.2)',
                fontSize: '15px', fontWeight: 700, letterSpacing: '1px',
                color: '#e8d48a', textTransform: 'uppercase', flexShrink: 0,
              }}>Output so far</div>
              <div style={{
                flex: 1, overflowY: 'auto', overflowX: 'auto', padding: '8px 12px',
                fontFamily: 'Consolas, Monaco, "Courier New", monospace',
                fontSize: '17px', color: '#86efac', textAlign: 'left',
              }}>
                {currentStep.stdout
                  ? currentStep.stdout.replace(/\n$/, '').split('\n').map((line, i) => (
                      <div key={i} style={{ minWidth: 'max-content', whiteSpace: 'pre', lineHeight: '1.6' }}>
                        {line || '\u00A0'}
                      </div>
                    ))
                  : <span style={{ color: '#4b5563' }}>(no output yet)</span>
                }
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

const navBtn = (disabled) => ({
  padding: '5px 14px',
  backgroundColor: disabled ? '#161b22' : '#21262d',
  color: disabled ? '#4b5563' : '#c9d1d9',
  border: disabled ? '1px solid #30363d' : '1px solid rgba(255, 240, 150, 0.3)',
  borderRadius: '6px',
  cursor: disabled ? 'not-allowed' : 'pointer',
  fontWeight: 600,
  fontSize: '17px',
});

export default VisualDebugger;
